import React, { useState } from "react";

import { SmallText } from "shared/components/Typography";
import type {
  Vero1CustomItemInput,
  Vero1PersistScopeOptions,
} from "shared/interfaces/api/vero1";

import {
  AddFieldButton,
  AddFieldLink,
  CancelLink,
  CompanyConfirmRow,
  CompanyWarning,
  CompanyWarningText,
  EditorContainer,
  EditorLabel,
  FieldHint,
  FormActions,
  FormActionsNote,
  FormFootnote,
  NewFieldEyebrow,
  NewFieldHeader,
  NewFieldNote,
  ScopeCard,
  ScopeDetail,
  ScopeIntro,
  ScopeList,
  ScopeRadio,
  ScopeSection,
  ScopeTitle,
  RequiredMark,
} from "./editorStyled";
import { CircleAlertGlyph } from "./icons";
import { TextInput } from "./reportStyled";
import { CalloutCard } from "./styled";

/**
 * Two inputs (spec §5.2): what the row is called and where it lives. No
 * category, no context — the agent assesses the seeded row like any other,
 * and the finding is written there, not here. The label caps at 45 characters
 * because it is a row title leasing sees verbatim.
 */
const LABEL_MAX = 45;

export interface AddChecklistItemProps {
  saving?: boolean;
  /** Fired when the form expands — the panel brings it into view, the way
   *  an opening row editor is centred. */
  onOpen?: () => void;
  /** Server-computed: a wider scope is absent when it covers nothing beyond
   *  the narrower one (single-property portfolio, single-portfolio company). */
  scopeOptions?: Vero1PersistScopeOptions;
  onAdd: (
    item: Vero1CustomItemInput,
    options: {
      onSuccess: (data?: { templateStatus?: string | null }) => void;
      onError: (error: {
        statusCode?: number;
        errors?: { error?: { label?: string[] } | string[] };
        response?: { status?: number; data?: { error?: unknown } };
      }) => void;
    }
  ) => void;
}

type PersistScope = "" | "property" | "portfolio" | "company";

/** "Scope is a real commitment" — every offer names its blast radius in the
 *  only terms that matter: properties where VERO1 actually runs. The total
 *  property count is noise (rows never seed on inactive properties), so it
 *  stays off the label; latent reach as properties activate is covered by the
 *  company acknowledgment. */
const scopeReach = (option?: {
  propertyCount: number;
  vero1ActiveCount: number;
}) =>
  option
    ? ` — applies to ${option.vero1ActiveCount} VERO1-active ${
        option.vero1ActiveCount === 1 ? "property" : "properties"
      }`
    : "";

/** Spec §5.2: the two rejections read differently — a duplicate on this
 *  checklist points at the existing row; anything else is a plain error.
 *  The axios layer rethrows as ApiError: statusCode + camelCased body on
 *  `errors` (raw axios `response.data` kept as a fallback). */
const rejectionMessage = (error: {
  errors?: { error?: { label?: string[] } | string[] };
  response?: { data?: { error?: unknown } };
}) => {
  const body = (error?.errors?.error ?? error?.response?.data?.error) as
    | { label?: string[] }
    | string[]
    | undefined;
  const labelError = Array.isArray(body) ? body[0] : body?.label?.[0];
  if (labelError && String(labelError).includes("already exists")) {
    return "Already on this checklist — edit that row instead of adding it again.";
  }
  return labelError ? String(labelError) : "The item could not be added.";
};

export const AddChecklistItem = ({
  saving,
  scopeOptions,
  onAdd,
  onOpen,
}: AddChecklistItemProps) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [persistScope, setPersistScope] = useState<PersistScope>("");
  const [companyConfirmed, setCompanyConfirmed] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);
  const [templateNotice, setTemplateNotice] = useState<string | null>(null);

  const reset = () => {
    setOpen(false);
    setLabel("");
    setPersistScope("");
    setCompanyConfirmed(false);
    setRejection(null);
  };

  const pickScope = (scope: PersistScope) => {
    setPersistScope(scope);
    // the acknowledgment is per-attempt: switching away and back re-asks
    if (scope !== "company") setCompanyConfirmed(false);
  };

  // company scope is the widest blast radius — Add stays disabled until the
  // agent acknowledges it in words (spec §5.3)
  const needsCompanyConfirm = persistScope === "company" && !companyConfirmed;

  if (!open) {
    return (
      <>
        {templateNotice && (
          <SmallText weak data-testid="vero1-template-notice">
            {templateNotice}
          </SmallText>
        )}
        <AddFieldLink
          type="button"
          data-testid="vero1-add-item-toggle"
          onClick={() => {
            setTemplateNotice(null);
            setOpen(true);
            onOpen?.();
          }}
        >
          + Add a field
        </AddFieldLink>
      </>
    );
  }

  return (
    <EditorContainer data-testid="vero1-add-item-form">
      <NewFieldHeader>
        <NewFieldEyebrow>New field</NewFieldEyebrow>
        <NewFieldNote>Not assessed by Lumen</NewFieldNote>
      </NewFieldHeader>

      <EditorLabel htmlFor="vero1-add-item-label">
        Field label <RequiredMark>*</RequiredMark>
      </EditorLabel>
      <TextInput
        id="vero1-add-item-label"
        aria-label="Field label"
        maxLength={LABEL_MAX}
        value={label}
        onChange={(event) => {
          setLabel(event.target.value);
          setRejection(null);
        }}
        placeholder="e.g. Pet policy acknowledgment"
      />
      <FieldHint>
        Max {LABEL_MAX} characters — this is a row title, not the finding.
        Leasing sees it verbatim.
      </FieldHint>
      {rejection && (
        <CalloutCard $role="guard" data-testid="vero1-add-item-rejection">
          <SmallText>{rejection}</SmallText>
        </CalloutCard>
      )}

      <ScopeSection>
        <EditorLabel>Include on future checklists</EditorLabel>
        <ScopeIntro>
          Future checklists get this label seeded blank, awaiting assessment.
        </ScopeIntro>
        <ScopeList data-testid="vero1-add-item-scope" role="radiogroup">
          <ScopeCard $selected={persistScope === ""}>
            <input
              type="radio"
              name="vero1-persist-scope"
              checked={persistScope === ""}
              onChange={() => pickScope("")}
            />
            <ScopeRadio $selected={persistScope === ""} />
            <div>
              <ScopeTitle>Just this checklist</ScopeTitle>
              <ScopeDetail>
                This deal only. Nothing changes for anyone else.
              </ScopeDetail>
            </div>
          </ScopeCard>
          <ScopeCard $selected={persistScope === "property"}>
            <input
              type="radio"
              name="vero1-persist-scope"
              data-testid="vero1-scope-property"
              checked={persistScope === "property"}
              onChange={() => pickScope("property")}
            />
            <ScopeRadio $selected={persistScope === "property"} />
            <div>
              <ScopeTitle>This property</ScopeTitle>
              {scopeOptions?.property && (
                <ScopeDetail>{scopeOptions.property.name}</ScopeDetail>
              )}
            </div>
          </ScopeCard>
          {scopeOptions?.portfolio && (
            <ScopeCard $selected={persistScope === "portfolio"}>
              <input
                type="radio"
                name="vero1-persist-scope"
                data-testid="vero1-scope-portfolio"
                checked={persistScope === "portfolio"}
                onChange={() => pickScope("portfolio")}
              />
              <ScopeRadio $selected={persistScope === "portfolio"} />
              <div>
                <ScopeTitle>This portfolio</ScopeTitle>
                <ScopeDetail>
                  {scopeOptions.portfolio.name}
                  {scopeReach(scopeOptions.portfolio)}
                </ScopeDetail>
              </div>
            </ScopeCard>
          )}
          {scopeOptions?.company && (
            <ScopeCard $warn $selected={persistScope === "company"}>
              <input
                type="radio"
                name="vero1-persist-scope"
                data-testid="vero1-scope-company"
                checked={persistScope === "company"}
                onChange={() => pickScope("company")}
              />
              <ScopeRadio $warn $selected={persistScope === "company"} />
              <div>
                <ScopeTitle>Entire client</ScopeTitle>
                <ScopeDetail>
                  {scopeOptions.company.name}
                  {scopeReach(scopeOptions.company)}
                </ScopeDetail>
              </div>
            </ScopeCard>
          )}
        </ScopeList>
      </ScopeSection>

      {persistScope === "company" && scopeOptions?.company && (
        <CompanyWarning data-testid="vero1-company-confirm">
          <CompanyWarningText>
            <CircleAlertGlyph size={14} tone="#b35500" />
            <p>
              Every VERO1-active property in {scopeOptions.company.name} starts
              getting this row, blank, on every new checklist — including teams
              you don&apos;t review for, and any property that activates VERO1
              later. Removing it later is a settings change, not an undo here.
            </p>
          </CompanyWarningText>
          <CompanyConfirmRow>
            <input
              type="checkbox"
              data-testid="vero1-company-confirm-check"
              checked={companyConfirmed}
              onChange={(event) => setCompanyConfirmed(event.target.checked)}
            />
            <span>
              Yes, add {label.trim() ? `“${label.trim()}”` : "this item"} to
              every checklist company-wide
            </span>
          </CompanyConfirmRow>
        </CompanyWarning>
      )}

      <FormActions>
        <AddFieldButton
          type="button"
          disabled={!label.trim() || needsCompanyConfirm || saving}
          onClick={() =>
            onAdd(
              {
                label: label.trim(),
                persistScope: persistScope || null,
              },
              {
                onSuccess: (data) => {
                  // the row seeded either way — "already included" only means
                  // the scope's template had it, so nothing new persists
                  if (data?.templateStatus === "already-included") {
                    setTemplateNotice(
                      `Already part of the ${persistScope} template — added to this checklist only.`
                    );
                  }
                  reset();
                },
                onError: (error) => {
                  const status = error?.statusCode ?? error?.response?.status;
                  if (status === 400) setRejection(rejectionMessage(error));
                },
              }
            )
          }
        >
          {saving ? "Adding…" : "Add field"}
        </AddFieldButton>
        <CancelLink type="button" onClick={reset}>
          Cancel
        </CancelLink>
        {needsCompanyConfirm && (
          <FormActionsNote>Confirm above to continue</FormActionsNote>
        )}
      </FormActions>
      <FormFootnote>
        Adds an unassessed row. Enter findings by editing it.
      </FormFootnote>
    </EditorContainer>
  );
};
